import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import cn from "classnames";
import {useSetting} from "../../contexts/setting";
import {usePixelationPalette} from "hooks/usePixelationPalette";
import {
  cellKey,
  describeCell,
  drawPixelation,
  getCellAt,
  toggleCorner,
} from "../../libs/pixelation";
import {
  fitCrossSize,
  getTitleFontSizeFromTemplate,
  titleSquareSize,
} from "../../libs/pixelationSizing";
import {
  computeDots,
  drawDots,
  meanColor,
  meanTone,
  toneFieldFromRgba,
} from "../../libs/pixelationDots";
import type {Raster, ToneField} from "../../libs/pixelationDots";
import {
  FULL_MASK,
  addStrokePoint,
  assertMask,
  drawMaskStroke,
  isFullMask,
  maskSampler,
  paintMask,
  startStroke,
} from "../../libs/pixelationMask";
import type {MaskStroke, PixelationMask} from "../../libs/pixelationMask";
import {backdropColorOf, rasterizeBackground} from "../../libs/backgroundRaster";
import "./index.css";

/**
 * Canvas effect drawn over the background image of a Releases banner. Sits
 * between the background (z-index 1) and the content/upload layers
 * (z-index ≥ 10), so it only ever covers the bg image — never text, logos,
 * or uploaded photos. Renders nothing unless the effect is enabled on a
 * Releases template.
 *
 * Two shapes (`pixelationShape`):
 *   - "squares": the mosaic that grows from one edge of the banner and
 *     dissolves toward the other (libs/pixelation.ts).
 *   - "dots": a halftone of round dots over the whole banner
 *     (libs/pixelationDots.ts). The "image" pattern sizes each dot by the tone
 *     of the background under it, read from the live background
 *     (libs/backgroundRaster.ts); the "random" pattern by seeded noise. A dot
 *     takes the colour of the background under it, or a palette colour.
 *
 * A per-template brush mask (libs/pixelationMask.ts) sets where the effect
 * shows, for both shapes. While the brush is on, a surface above the banner
 * takes the pointer and paints strokes; a finished stroke goes into the scene.
 *
 * The canvas backing store is sized to the template's layout pixels
 * (offsetWidth/offsetHeight, unaffected by preview zoom), so square sizes are
 * stable across zoom levels and match the exported PNG captured from the live
 * canvas by Playwright.
 *
 * Tile size is the banner's TITLE font size × the coefficient (see
 * titleSquareSize) — read from this template's own `.title`, so the @2x splash
 * variant (2× canvas AND 2× title font) gets 2× tiles automatically; no
 * per-instance scale factor is needed. Tile COUNTS differ across templates by
 * design (each is sized to its own title). The size is a single scalar, so it's
 * unaffected by direction (rotating only changes which axis fades) and by
 * coverage (the Coverage slider adds/removes rows of same-size tiles, extending
 * the mosaic toward the fade edge, never rescaling them). A locked template can
 * still pin an explicit px size via the per-template override.
 *
 * That scalar is the FADE-axis edge. The perpendicular (cross) edge is fitted
 * per-render (fitCrossSize) to a whole-tile division of the full-span axis, so
 * the last row/column reaches the canvas edge exactly instead of overrunning and
 * being clipped. The fit reuses the square-layout tile count, so it never shifts
 * which cell a per-template `row:col` override lands on.
 */
/** Every template grows from the edge of the global pixelationDirection (the
 * panel's "From", left by default). An unchained (locked) template can rotate
 * its own direction 90° at a time (ltr → ttb → rtl → btt) via the rotate
 * control; the chosen direction is stored per template in
 * pixelationDirectionOverrides and honoured only while the template is locked
 * (parity with the per-template tile-size override). */
// Stable empty map for templates with no click overrides yet — a shared frozen
// reference so drawParams' identity only changes when real overrides change.
const EMPTY_OVERRIDES = Object.freeze({});

// The background is read at most this many px on its long side: enough for
// cell means, cheap to read again when the background moves, and small to
// keep (about 2 MB per banner with its tone table).
const TONE_MAX_SIDE = 512;
// The mask raster, long side px.
const MASK_MAX_SIDE = 1024;
// The smallest dot grid pitch, px of a 1× banner.
const MIN_DOT_PITCH = 3;

/** The background pixels and their tone, `scale` sample px per layout px. */
type ToneSample = {raster: Raster; field: ToneField; scale: number};

type MaskCache = {
  mask: Readonly<PixelationMask>;
  width: number;
  height: number;
  canvas: HTMLCanvasElement;
  data: Uint8ClampedArray;
};

/** The `.bg-transformer` this layer covers: a sibling, else the template's. */
function findBgRoot(canvas: HTMLElement): HTMLElement | null {
  const sibling = canvas.parentElement?.querySelector<HTMLElement>(":scope > .bg-transformer");
  if (sibling) return sibling;
  return canvas.closest(".template")?.querySelector<HTMLElement>(".bg-transformer") ?? null;
}

/** The layout size the canvas covers (see the draw effect for why). */
function layoutSize(canvas: HTMLCanvasElement): [number, number] {
  const parent = canvas.parentElement as HTMLElement | null;
  let w = parent?.offsetWidth || 0;
  let h = parent?.offsetHeight || 0;
  if (!w || !h) {
    const tpl = canvas.closest<HTMLElement>(".template");
    w = tpl?.offsetWidth || canvas.offsetWidth;
    h = tpl?.offsetHeight || canvas.offsetHeight;
  }
  return [w, h];
}

function newCanvas(width: number, height: number): HTMLCanvasElement {
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  return canvas;
}

const PixelationLayer = ({scale = 1}: {scale?: number} = {}) => {
  const {
    locked,
    layoutOption,
    titleFontSize: titleFontSizeSetting,
    pixelationEnabled,
    pixelationSizeCoefficient,
    pixelationSizeOverrides,
    pixelationCoverageOverrides,
    pixelationDirectionOverrides,
    pixelationDirection,
    pixelationOpacity,
    pixelationCoverage,
    pixelationGradual,
    pixelationOpacityRamp,
    pixelationBlend,
    pixelationSeed,
    pixelationCells,
    pixelationClickAction,
    pixelationColors,
    pixelationTileShape,
    pixelationTileRoundness,
    pixelationTileRadius,
    pixelationShapes,
    pixelationShape,
    pixelationDotPattern,
    pixelationDotSizeCoefficient,
    pixelationDotScale,
    pixelationDotTone,
    pixelationDotInvert,
    pixelationDotColor,
    pixelationDotStructure,
    pixelationDotBackdrop,
    pixelationMasks,
    pixelationBrush,
    pixelationBrushSize,
    pixelationBrushSoftness,
    togglePixelationCell,
    cyclePixelationColor,
    setPixelationCellShape,
    addPixelationMaskStroke,
    dataChannel,
  } = useSetting();

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const surfaceRef = useRef<HTMLDivElement>(null);
  const ringRef = useRef<HTMLDivElement>(null);
  // The id of the .template this layer lives in, read from the DOM. Used to
  // look up a per-template base tile size override (set via TemplateControls)
  // and to scope click overrides to this banner.
  const [templateId, setTemplateId] = useState<string | null>(null);
  // The template's measured title font size (px), the basis for the tile size.
  // Read from the live `.title` element (see the effect below).
  const [titleFontPx, setTitleFontPx] = useState(80);
  // The background pixels for the image pattern and image colours; null until
  // they can be read.
  const toneRef = useRef<ToneSample | null>(null);
  const maskCacheRef = useRef<MaskCache | null>(null);
  // The stroke under the pointer, not yet in the scene.
  const liveStrokeRef = useRef<MaskStroke | null>(null);
  const liveCanvasRef = useRef<HTMLCanvasElement | null>(null);
  // The latest draw, for callers outside React's render (observers, pointer).
  const renderRef = useRef<() => void>(() => {});
  const frameRef = useRef(0);

  // The mosaic is a Releases-only feature. Several Releases templates reuse the
  // shared Social and Email components (Twitter, SoMeVertical, Email01, …), so
  // gate on the channel here to keep the effect from leaking into those
  // channels if the setting is left enabled when switching channels.
  const active = pixelationEnabled && dataChannel === "releases";
  const isDots = pixelationShape === "dots";
  const needsTone =
    active && isDots && (pixelationDotPattern === "image" || pixelationDotColor === "image");
  const brushOn = active && pixelationBrush !== "off";

  // Palette resolution (incl. bg sampling + per-source overrides) lives in the
  // shared hook so the toolbar swatches and the canvas always agree.
  const {palette} = usePixelationPalette();

  // An unchained (locked) template can pin its own square size, overriding the
  // shared one. Only honoured while the template is locked — re-syncing it makes
  // the mosaic follow the shared size again (the saved override stays put for
  // when it's detached next). NOTE: an override breaks pattern-alignment for
  // that template by design — it's an intentional per-banner opt-out.
  const isLocked = templateId ? locked.some((x) => x === templateId) : false;
  const sizeOverride = isLocked && templateId ? pixelationSizeOverrides?.[templateId] : undefined;

  // Fade direction: the global pixelationDirection (the edge the mosaic grows
  // from). A locked template can pin its own rotated direction (see
  // pixelationDirectionOverrides); like the size override it's honoured only
  // while the template is locked, so unchaining it snaps the mosaic back to the
  // global direction.
  const direction =
    (isLocked && templateId && pixelationDirectionOverrides?.[templateId]) || pixelationDirection;

  // Coverage: the global slider value unless this (locked) template pins its own
  // (see pixelationCoverageOverrides). Same locked-only gating as size/direction,
  // so unchaining snaps the mosaic back to the global coverage.
  const coverageOverride =
    isLocked && templateId ? pixelationCoverageOverrides?.[templateId] : undefined;
  const coverage = coverageOverride > 0 ? coverageOverride : Number(pixelationCoverage);

  // Fade-axis tile edge (px): the title font × coefficient (see titleSquareSize),
  // or the pinned per-template override. `scale` (2 for the @2x splash variants,
  // 1 otherwise) is applied to BOTH so the popover's size value — which the user
  // sets in the base 1× space — renders at the same 2× the default does. Without
  // scaling the override, a locked @2x template's pinned size dropped to half
  // (the "tiny tiles" bug). This is the edge along the dissolve direction; it
  // carries the coefficient/override and is identical in every direction and
  // unaffected by coverage. The perpendicular (cross) edge is derived per-render
  // via fitCrossSize (see the draw effect) so the last row/column tiles the
  // full-span axis exactly instead of overrunning it and being clipped.
  const size = useMemo(
    () =>
      (sizeOverride > 0 ? sizeOverride : titleSquareSize(titleFontPx, pixelationSizeCoefficient)) *
      scale,
    [sizeOverride, titleFontPx, pixelationSizeCoefficient, scale]
  );

  // Dot grid pitch (px): the title font × the dots coefficient, × `scale` for
  // the @2x variants, as for the squares.
  const dotPitch =
    Math.max(MIN_DOT_PITCH, titleFontPx * Number(pixelationDotSizeCoefficient)) * scale;

  const savedMask = templateId ? pixelationMasks?.[templateId] : undefined;
  if (templateId && savedMask) assertMask(savedMask, templateId);
  const mask: Readonly<PixelationMask> = savedMask || FULL_MASK;

  // Build the param object once — shared by draw and hit-test. `size` and
  // `direction` are spread in at each call site.
  const drawParams = useMemo(
    () => ({
      opacity: Number(pixelationOpacity),
      coverage,
      gradual: pixelationGradual,
      opacityRamp: pixelationOpacityRamp,
      palette,
      seed: pixelationSeed,
      // Per-template click overrides: only this banner's edits, so a click on
      // one template doesn't bleed onto the others that share the pattern.
      overrides: (templateId && pixelationCells[templateId]) || EMPTY_OVERRIDES,
      colorOverrides: (templateId && pixelationColors[templateId]) || EMPTY_OVERRIDES,
      tileShape: pixelationTileShape,
      tileRoundness: Number(pixelationTileRoundness),
      tileRadius: Number(pixelationTileRadius),
      shapeOverrides: (templateId && pixelationShapes[templateId]) || EMPTY_OVERRIDES,
    }),
    [
      templateId,
      pixelationOpacity,
      coverage,
      pixelationGradual,
      pixelationOpacityRamp,
      palette,
      pixelationSeed,
      pixelationCells,
      pixelationColors,
      pixelationTileShape,
      pixelationTileRoundness,
      pixelationTileRadius,
      pixelationShapes,
    ]
  );

  // Translate a click into the grid cell under the cursor, then act on it per
  // the active click action: "color" cycles the square's palette color, "shape"
  // makes the corner under the cursor round or sharp (the centre: all corners),
  // anything else cycles its opacity. The canvas backing store is sized in layout pixels
  // while the element is displayed at preview zoom, so scale the pointer
  // position by width/clientWidth to land in backing-store space before
  // hit-testing. Squares only: a dot has no click action.
  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLCanvasElement>) => {
      if (isDots) return;
      const canvas = canvasRef.current;
      if (!canvas || !canvas.width || !canvas.height) return;
      const rect = canvas.getBoundingClientRect();
      if (!rect.width || !rect.height) return;
      const px = ((e.clientX - rect.left) / rect.width) * canvas.width;
      const py = ((e.clientY - rect.top) / rect.height) * canvas.height;
      // Fit the cross edge to the same axis the draw effect uses (full width for
      // vertical dissolves, full height otherwise) so clicks hit-test against the
      // exact squares that were painted.
      const vertical = direction === "btt" || direction === "ttb";
      const sizeCross = fitCrossSize(vertical ? canvas.width : canvas.height, size);
      const params = {...drawParams, size, sizeCross, direction};
      const hit = getCellAt(px, py, canvas.width, canvas.height, params);
      if (!hit) return;
      // Attribute the click to this template only. Without an id we can't store
      // a per-template override, so ignore the click rather than leak it global.
      if (!templateId) return;
      const key = cellKey(hit.row, hit.col);
      if (pixelationClickAction === "color") cyclePixelationColor(templateId, key);
      else if (pixelationClickAction === "shape") {
        const cell = describeCell(hit.row, hit.col, canvas.width, canvas.height, params);
        const corners = toggleCorner(
          cell.corners,
          (px - cell.xL) / cell.w,
          (py - cell.yT) / cell.h
        );
        setPixelationCellShape(templateId, key, corners);
      } else togglePixelationCell(templateId, key);
    },
    [
      isDots,
      templateId,
      drawParams,
      size,
      direction,
      pixelationClickAction,
      togglePixelationCell,
      cyclePixelationColor,
      setPixelationCellShape,
    ]
  );

  // Read the host template's id from the DOM once active, so we can scope size
  // and click overrides to this banner.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !active) return;

    const template = canvas.closest(".template");
    if (template?.id) setTemplateId(template.id);
  }, [active]);

  // Measure the template's title font size — the tile is scaled to it. Re-read
  // when the effect activates and when something that changes the title font
  // does (layout switch, the title-size setting). Auto-fit shrink from very long
  // titles isn't tracked (rare; matches prior behavior).
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !active) return;
    const template = canvas.closest(".template");
    if (template) setTitleFontPx(getTitleFontSizeFromTemplate(template));
  }, [active, layoutOption, titleFontSizeSetting]);

  // The mask raster of this banner at (w × h), or null for the full mask with
  // no stroke in progress. The committed strokes are cached per mask object;
  // a stroke in progress is drawn over a copy of them.
  const maskRaster = useCallback(
    (w: number, h: number): {canvas: HTMLCanvasElement; data: Uint8ClampedArray} | null => {
      const live = liveStrokeRef.current;
      if (!live && isFullMask(mask)) return null;
      const k = Math.min(1, MASK_MAX_SIDE / Math.max(w, h));
      const mw = Math.max(1, Math.round(w * k));
      const mh = Math.max(1, Math.round(h * k));
      let cache = maskCacheRef.current;
      if (!cache || cache.mask !== mask || cache.width !== w || cache.height !== h) {
        const canvas = newCanvas(mw, mh);
        const ctx = canvas.getContext("2d", {willReadFrequently: true});
        if (!ctx) throw new Error("PixelationLayer: no 2D context for the mask");
        paintMask(ctx, w, h, k, mask);
        cache = {mask, width: w, height: h, canvas, data: ctx.getImageData(0, 0, mw, mh).data};
        maskCacheRef.current = cache;
      }
      if (!live) return cache;
      let work = liveCanvasRef.current;
      if (!work || work.width !== mw || work.height !== mh) {
        work = newCanvas(mw, mh);
        liveCanvasRef.current = work;
      }
      const ctx = work.getContext("2d", {willReadFrequently: true});
      if (!ctx) throw new Error("PixelationLayer: no 2D context for the mask");
      ctx.globalCompositeOperation = "copy";
      ctx.drawImage(cache.canvas, 0, 0);
      ctx.globalCompositeOperation = "source-over";
      drawMaskStroke(ctx, live, w, h, k);
      return {canvas: work, data: ctx.getImageData(0, 0, mw, mh).data};
    },
    [mask]
  );

  // Draw / redraw whenever any input changes. A ResizeObserver re-runs the
  // draw when the template is resized so the canvas resolution tracks it.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !active) return;

    const render = () => {
      // Backing store is sized in layout pixels (unaffected by preview zoom) so
      // squares stay stable and match the exported PNG. Prefer the immediate
      // parent, but fall back to the closest .template ancestor — some templates
      // (e.g. the Wallpaper preview) wrap the background in a div that collapses
      // to zero height around the absolutely-positioned bg, and the canvas fills
      // .template (the positioned ancestor) anyway.
      const [w, h] = layoutSize(canvas);
      if (!w || !h) return;
      if (canvas.width !== w) canvas.width = w;
      if (canvas.height !== h) canvas.height = h;
      const ctx = canvas.getContext("2d");
      if (!ctx) return;
      const raster = maskRaster(w, h);
      const maskAt = raster
        ? maskSampler(raster.data, raster.canvas.width, raster.canvas.height, w, h)
        : null;

      if (!isDots) {
        // The cross axis is the one perpendicular to the dissolve: full width for
        // vertical dissolves (btt/ttb), full height otherwise (ltr/rtl). Fit its
        // tile edge to that extent so the last row/column reaches the edge exactly
        // rather than overrunning and being clipped. `size` (the fade edge) is
        // untouched — its far edge is the dissolve, where clipping is hidden.
        const vertical = direction === "btt" || direction === "ttb";
        const sizeCross = fitCrossSize(vertical ? w : h, size);
        drawPixelation(ctx, w, h, {
          ...drawParams,
          size,
          sizeCross,
          direction,
          ...(maskAt && {mask: maskAt}),
        });
        return;
      }

      ctx.clearRect(0, 0, w, h);
      const tone = toneRef.current;
      // The image pattern and image colours wait for the background pixels
      // (still loading, or a generative background, which has none).
      if (needsTone && !tone) return;

      // The backdrop hides the background where the mask shows the dots, so
      // the image turns into dots instead of getting dots over it.
      const bgRoot = findBgRoot(canvas);
      const backdrop =
        pixelationDotBackdrop === "none"
          ? null
          : pixelationDotBackdrop === "base"
            ? bgRoot
              ? backdropColorOf(bgRoot)
              : null
            : pixelationDotBackdrop;
      if (backdrop) {
        ctx.save();
        if (raster) {
          ctx.drawImage(raster.canvas, 0, 0, w, h);
          ctx.globalCompositeOperation = "source-in";
        }
        ctx.fillStyle = backdrop;
        ctx.fillRect(0, 0, w, h);
        ctx.restore();
      }

      const k = tone?.scale ?? 1;
      const dots = computeDots(
        w,
        h,
        {
          pitch: dotPitch,
          dotScale: Number(pixelationDotScale),
          pattern: pixelationDotPattern,
          tone: Number(pixelationDotTone),
          invert: Boolean(pixelationDotInvert),
          structure: Number(pixelationDotStructure),
          seed: pixelationSeed,
          color: pixelationDotColor,
          palette,
        },
        {
          toneAt: tone
            ? (x0, y0, x1, y1) => meanTone(tone.field, x0 * k, y0 * k, x1 * k, y1 * k)
            : null,
          colorAt: tone
            ? (x0, y0, x1, y1) => meanColor(tone.raster, x0 * k, y0 * k, x1 * k, y1 * k)
            : null,
          maskAt,
        }
      );
      drawDots(ctx, dots, Number(pixelationOpacity));
    };

    renderRef.current = render;
    render();

    const parent = canvas.parentElement;
    if (!parent || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(() => render());
    ro.observe(parent);
    return () => ro.disconnect();
  }, [
    active,
    drawParams,
    size,
    direction,
    isDots,
    maskRaster,
    dotPitch,
    palette,
    needsTone,
    pixelationSeed,
    pixelationOpacity,
    pixelationDotPattern,
    pixelationDotScale,
    pixelationDotTone,
    pixelationDotInvert,
    pixelationDotColor,
    pixelationDotStructure,
    pixelationDotBackdrop,
  ]);

  const scheduleRender = useCallback(() => {
    if (frameRef.current) return;
    frameRef.current = requestAnimationFrame(() => {
      frameRef.current = 0;
      renderRef.current();
    });
  }, []);
  useEffect(() => () => cancelAnimationFrame(frameRef.current), []);

  // Read the background tone for the image pattern, and read it again when the
  // background changes: a picture loads, its crop moves or animates, the
  // banner resizes, or the background is mounted again.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !needsTone) {
      toneRef.current = null;
      return;
    }
    const host = canvas.closest<HTMLElement>(".template") || canvas.parentElement;
    if (!host) return;
    let frame = 0;
    const sample = () => {
      frame = 0;
      const bgRoot = findBgRoot(canvas);
      const [w, h] = layoutSize(canvas);
      if (!bgRoot || !w || !h) {
        toneRef.current = null;
      } else {
        const k = Math.min(1, TONE_MAX_SIDE / Math.max(w, h));
        const result = rasterizeBackground(bgRoot, canvas, w, h, k);
        if (result.ok) {
          toneRef.current = {
            raster: result,
            field: toneFieldFromRgba(result.data, result.width, result.height),
            scale: result.width / w,
          };
        } else {
          toneRef.current = null;
          if (result.reason === "tainted") {
            console.error(
              "Pixelation dots: the background image has no CORS headers, so its pixels cannot be read"
            );
          }
        }
      }
      renderRef.current();
    };
    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(sample);
    };
    const inBackground = (node: Node | null) =>
      node instanceof Element && !!node.closest(".bg-transformer");
    // A load reads the pixels at once, not on the next frame: the export
    // renderer takes its screenshot soon after the background image is ready.
    const onMediaEvent = (e: Event) => {
      if (!inBackground(e.target as Node)) return;
      cancelAnimationFrame(frame);
      sample();
    };
    sample();
    host.addEventListener("load", onMediaEvent, true);
    host.addEventListener("loadeddata", onMediaEvent, true);
    host.addEventListener("transitionend", onMediaEvent, true);
    const mo = new MutationObserver((records) => {
      for (const r of records) {
        const changed =
          inBackground(r.target) ||
          (r.type === "childList" &&
            r.target instanceof Element &&
            !!r.target.querySelector(".bg-transformer"));
        if (changed) {
          schedule();
          return;
        }
      }
    });
    mo.observe(host, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ["style", "src", "class"],
    });
    const ro = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(schedule);
    ro?.observe(host);
    return () => {
      cancelAnimationFrame(frame);
      host.removeEventListener("load", onMediaEvent, true);
      host.removeEventListener("loadeddata", onMediaEvent, true);
      host.removeEventListener("transitionend", onMediaEvent, true);
      mo.disconnect();
      ro?.disconnect();
      toneRef.current = null;
    };
  }, [needsTone]);

  // ── Brush ────────────────────────────────────────────────────────────────
  // Brush diameter in this banner's layout px: the @2x variants get a 2× brush,
  // so the brush looks the same size on every banner.
  const brushDiameter = Number(pixelationBrushSize) * scale;

  const pointerToLayout = (e: React.PointerEvent<HTMLDivElement>) => {
    const surface = surfaceRef.current;
    const canvas = canvasRef.current;
    if (!surface || !canvas || !canvas.width || !canvas.height) return null;
    const rect = surface.getBoundingClientRect();
    if (!rect.width || !rect.height) return null;
    return {
      x: ((e.clientX - rect.left) / rect.width) * canvas.width,
      y: ((e.clientY - rect.top) / rect.height) * canvas.height,
      w: canvas.width,
      h: canvas.height,
    };
  };

  const moveRing = (x: number, y: number) => {
    const ring = ringRef.current;
    if (!ring) return;
    ring.style.display = "block";
    ring.style.transform = `translate(${x - brushDiameter / 2}px, ${y - brushDiameter / 2}px)`;
  };

  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    // The whole banner is a drag handle (@dnd-kit); a stroke must not move it.
    e.stopPropagation();
    if (e.button !== 0 || !templateId) return;
    const p = pointerToLayout(e);
    if (!p) return;
    e.preventDefault();
    // Alt paints with the other tool, as in image editors.
    const value: 0 | 1 = (pixelationBrush === "show") !== e.altKey ? 1 : 0;
    liveStrokeRef.current = startStroke(
      value,
      p.x,
      p.y,
      brushDiameter / 2,
      Number(pixelationBrushSoftness) / 100,
      p.w,
      p.h
    );
    e.currentTarget.setPointerCapture(e.pointerId);
    scheduleRender();
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const p = pointerToLayout(e);
    if (!p) return;
    moveRing(p.x, p.y);
    const stroke = liveStrokeRef.current;
    if (!stroke) return;
    const step = Math.max(1, brushDiameter * 0.08);
    if (addStrokePoint(stroke, p.x, p.y, p.w, p.h, step)) scheduleRender();
  };

  const finishStroke = () => {
    const stroke = liveStrokeRef.current;
    if (!stroke || !templateId) return;
    liveStrokeRef.current = null;
    addPixelationMaskStroke(templateId, stroke);
  };

  const hideRing = () => {
    if (ringRef.current) ringRef.current.style.display = "none";
  };

  // A stroke in progress ends with the brush.
  useEffect(() => {
    if (!brushOn) liveStrokeRef.current = null;
  }, [brushOn]);

  if (!active) return null;

  return (
    <>
      <canvas
        ref={canvasRef}
        className={cn("pixelation-layer", {"pixelation-layer--dots": isDots})}
        style={{mixBlendMode: pixelationBlend}}
        onClick={handleClick}
        aria-hidden="true"
      />
      {brushOn && (
        <div
          ref={surfaceRef}
          className="pixelation-brush-surface"
          data-brush={pixelationBrush}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={finishStroke}
          onPointerCancel={finishStroke}
          onPointerLeave={hideRing}
          aria-hidden="true"
        >
          <div
            ref={ringRef}
            className="pixelation-brush-ring"
            style={{width: brushDiameter, height: brushDiameter}}
          />
        </div>
      )}
    </>
  );
};

export default PixelationLayer;
